import * as React from "react";

import ErrorComponent from "./ErrorComponent";
import DatasetAuthorizationWrapper from "src/common/DatasetAuthorizationWrapper";

import CoreAPI from "./CoreAPI";
import AssessmentResultsAPI from "src/admin/results/AssessmentResultsAPI";
import PickListContext from "src/context/pickListContext";
import fleetErrorAPI from "src/admin/Organization/OrganizationError/FleetErrorAPI";
import { Button } from "src/components/buttons";
import { ClientPermissions } from "src/common/Permissions";
import { Spinner } from "src/components/common";
import { Prompt } from "react-router-dom";
import { toast } from "react-toastify";

interface IError {
	id: number;
	code: string;
	description: string;
	count: number;
}

interface IProps {
	location?: any;
	match?: any;
	history?: any;
	api?: any;
}

interface IState {
	clientErrorEntries: CoreErrorEntryDTO[];
	fleetErrors: FleetErrorDTO[];
	errorCount: number;
	coreTest: CoreTestResultDTO;
	isLoading: boolean;
	isDirty: boolean;
	completeClicked: boolean;
	dataLocked: boolean;
}

class CoreErrorEntry extends React.Component<IProps, IState> {
	public static contextType = PickListContext;
	public state: IState = {
		clientErrorEntries: [],
		fleetErrors: [],
		errorCount: 0,
		coreTest: {},
		isLoading: true,
		isDirty: false,
		completeClicked: false,
		dataLocked: true,
	};

	public componentDidMount() {
		const { coreId, clientId, datasetId, sessionId } = this.props.match.params;

		CoreAPI.getCoreTest({
			datasetId,
			clientId,
			coreTestId: coreId,
		}).then((test) => this.setState({ coreTest: test, isLoading: false }));

		CoreAPI.getClientSiteClientCoreErrorEntries({
			datasetId,
			coreTestResultId: coreId,
		}).then((coreErrors) => {
			const errorCount = this.updateErrorCount(coreErrors);

			this.setState({
				clientErrorEntries: coreErrors,
				errorCount,
				isLoading: false,
			});
		});

		fleetErrorAPI
			.getClientSiteFleetErrors({ datasetId, coreTestResultId: coreId })
			.then((allErrors) => {
				this.setState({ fleetErrors: allErrors, isLoading: false });
			});

		if (+sessionId === 0) {
			this.setState({ dataLocked: false });
		} else {
			AssessmentResultsAPI.getSession({ datasetId, clientId, sessionId }).then(
				(session) => {
					this.setState({
						dataLocked: session.dataLocked === false ? false : true,
					});
				}
			);
		}
	}

	public componentDidUpdate(_prevProps: any, prevState: any) {
		if (
			this.state.clientErrorEntries !== prevState.clientErrorEntries &&
			this.state.clientErrorEntries.length ===
				prevState.clientErrorEntries.length &&
			this.state.coreTest.id === prevState.coreTest.id &&
			!this.state.isDirty
		) {
			this.setState({ isDirty: true });
		}
	}

	public render() {
		const clientErrorEntries = this.renderClientErrors(
			this.state.clientErrorEntries
		);
		const errorCountLabelAndValue = this.errorCountLabelAndValue();
		const { dataLocked } = this.state;

		if (this.state.isLoading) return <Spinner />;
		return (
			<div className="container-fluid">
				<Prompt when={this.state.isDirty} message="Exit without saving?" />
				<div className="row">{clientErrorEntries}</div>
				<div className="mt-2 mb-3">
					{this.state.errorCount > 0 ? errorCountLabelAndValue : null}
				</div>
				<DatasetAuthorizationWrapper
					type="hide"
					permissions={[ClientPermissions.EditCoreTest]}
				>
					<React.Fragment>
						<Button
							onClick={this.backToPreviousPage}
							disabled={dataLocked || this.state.completeClicked}
							className="btn-outline-primary mr-3"
						>
							Back
						</Button>
						<Button
							className="btn-primary"
							disabled={dataLocked || this.state.completeClicked}
							onClick={this.submitExcelerate}
						>
							Complete
						</Button>
					</React.Fragment>
				</DatasetAuthorizationWrapper>
			</div>
		);
	}

	private renderClientErrors = (clientErrors: CoreErrorEntryDTO[]) => {
		const { dataLocked } = this.state;
		clientErrors.sort((a, b) => a.skillCategoryId! - b.skillCategoryId!);
		return clientErrors.map((error: CoreErrorEntryDTO) => (
			<div className="col-md-6 col-lg-4" key={error.skillCategoryId}>
				<ErrorComponent
					error={error}
					addErrorToList={this.addErrorItem}
					removeErrorFromList={this.removeErrorFromList}
					addErrorScore={this.addErrorScore}
					fleetErrors={this.state.fleetErrors}
					dataLocked={dataLocked}
				/>
			</div>
		));
	};

	private errorCountLabelAndValue = () => {
		return (
			<p className="h3">
				<strong>Error Count:</strong>&nbsp;
				<span className="text-danger">{`${this.state.errorCount}`}</span>
			</p>
		);
	};

	private addErrorScore = (e: any, errorId: number) => {
		const clientErrorEntries = [...this.state.clientErrorEntries];
		const foundScoreClientError = clientErrorEntries.find(
			(item) => item.skillCategoryId === errorId
		);

		if (foundScoreClientError !== undefined) {
			const newClientErrorEntries = this.updateClientErrorWithNewScore(
				foundScoreClientError,
				e.key,
				clientErrorEntries,
				errorId
			);
			this.setState({ clientErrorEntries: newClientErrorEntries });
		}
	};

	private updateClientErrorWithNewScore = (
		coreError: CoreErrorEntryDTO,
		score: number,
		coreErrorEntries: CoreErrorEntryDTO[],
		errorId: number
	) => {
		const newClientError = {
			...coreError,
			scoreId: score,
		} as CoreErrorEntryDTO;
		const newClientErrorEntries = coreErrorEntries.filter(
			(item) => item.skillCategoryId !== errorId
		);
		const updatedClientErrorEntries = [
			...newClientErrorEntries,
			newClientError,
		] as CoreErrorEntryDTO[];

		updatedClientErrorEntries.sort(
			(a, b) => a.skillCategoryId! - b.skillCategoryId!
		);
		return updatedClientErrorEntries;
	};

	private increaseErrorItemCount = (
		error: IError,
		countValue: PickListItemResponseDTO
	) => {
		const oldCount = error.count;
		const newCount = oldCount + Number(countValue.description);
		return { ...error, count: newCount } as IError;
	};

	private decreaseErrorItemCount = (errorCode: ErrorCodeValuesDTO) => {
		const newCount = errorCode.count! - 1;
		return { ...errorCode, count: newCount } as IError;
	};

	private filterOldErrorItem = (
		listOfIErrors: IError[],
		coreErrorEntry: CoreErrorEntryDTO,
		updatedItem: IError,
		coreEntries: CoreErrorEntryDTO[]
	) => {
		const filteredClientErrors = listOfIErrors.filter(
			(err) => err.id !== updatedItem.id
		);
		const updatedClientErrorItems = [...filteredClientErrors, updatedItem];
		const updatedClientError = {
			...coreErrorEntry,
			values: updatedClientErrorItems,
		} as CoreErrorEntryDTO;
		const updatedClientErrorEntries = coreEntries.filter(
			(err) => err.skillCategoryId !== updatedClientError.skillCategoryId
		);
		const newClientErrorEntries = [
			...updatedClientErrorEntries,
			updatedClientError,
		];
		newClientErrorEntries.sort(
			(a, b) => a.skillCategoryId! - b.skillCategoryId!
		);
		return newClientErrorEntries;
	};

	private filterErrorCodeItem = (
		errorCodes: ErrorCodeValuesDTO[],
		error: IError,
		foundError: CoreErrorEntryDTO,
		currentErrorEntries: CoreErrorEntryDTO[]
	) => {
		const indexOfErrorValue = errorCodes.findIndex(
			(err) => err.id === error.id
		);
		const updatedErrorValues = errorCodes;
		updatedErrorValues[indexOfErrorValue] = error;
		const updatedErrorEntry = { ...foundError, values: updatedErrorValues };
		const indexOfClientErrorEntry = currentErrorEntries.findIndex(
			(errorEntry) =>
				errorEntry.skillCategoryId === updatedErrorEntry.skillCategoryId!
		);
		const updatedClientErrorEntries = currentErrorEntries;
		updatedClientErrorEntries[indexOfClientErrorEntry] = updatedErrorEntry;
		updatedClientErrorEntries.sort(
			(a, b) => a.skillCategoryId! - b.skillCategoryId!
		);
		return updatedClientErrorEntries;
	};

	private createNewErrorValue = (
		option: any,
		count: PickListItemResponseDTO
	) => {
		const errorCodeIndex = option.label.indexOf(".");
		const errorCode = option.label.slice(0, errorCodeIndex);
		const errorDescription = option.label.slice(
			errorCodeIndex + 1,
			option.label.length
		);
		const newCount = +count.description!;

		return {
			count: newCount,
			code: errorCode,
			description: errorDescription,
			id: Number(option.key),
		} as IError;
	};

	private addErrorItem = (option: any, count: number, errorId: number) => {
		const currentClientErrorEntries = [...this.state.clientErrorEntries];
		const foundClientError = currentClientErrorEntries.find(
			(err) => err.skillCategoryId === errorId
		) as CoreErrorEntryDTO;

		const newCountValue = this.context.pickListItems.find(
			(pItem: PickListItemResponseDTO) => pItem.id === count
		);

		if (foundClientError !== undefined) {
			const currentFoundErrors = foundClientError.values as IError[];
			const foundItem = currentFoundErrors.find(
				(err) => err.id === Number(option.key)
			);

			if (foundItem !== undefined) {
				const updatedItem = this.increaseErrorItemCount(
					foundItem,
					newCountValue
				);
				const sortedErrorEntries = this.filterOldErrorItem(
					currentFoundErrors,
					foundClientError,
					updatedItem,
					currentClientErrorEntries
				);

				// update error count label with amount of errors chosen
				const amountOfErrors = this.updateErrorCount(sortedErrorEntries);
				this.setState({
					clientErrorEntries: sortedErrorEntries,
					errorCount: amountOfErrors,
				});
				return;
			} else {
				const newErrorValues = this.createNewErrorValue(option, newCountValue);
				const updatedClientErrorItems = [...currentFoundErrors, newErrorValues];

				const updatedClientError = {
					...foundClientError,
					values: updatedClientErrorItems,
				} as CoreErrorEntryDTO;
				const filteredClientErrorEntries = currentClientErrorEntries.filter(
					(err) => err.skillCategoryId !== foundClientError.skillCategoryId
				);
				const updatedClientErrorEntries = [
					...filteredClientErrorEntries,
					updatedClientError,
				];

				updatedClientErrorEntries.sort(
					(a, b) => a.skillCategoryId! - b.skillCategoryId!
				);

				// update error count label with amount of errors chosen
				const amountOfErrors = this.updateErrorCount(updatedClientErrorEntries);
				this.setState({
					clientErrorEntries: updatedClientErrorEntries,
					errorCount: amountOfErrors,
				});
				return;
			}
		}
	};

	private removeErrorFromList = (
		errorEntryId: number,
		errorValueId: number
	) => {
		const currentClientErrorEntries = [...this.state.clientErrorEntries];
		const foundErrorEntry = currentClientErrorEntries.find(
			(err) => err.skillCategoryId === errorEntryId
		);

		if (foundErrorEntry !== undefined) {
			const foundErrorValues = foundErrorEntry.values!;
			const foundErrorValue = foundErrorValues.find(
				(err) => err.id === errorValueId
			);

			if (foundErrorValue !== undefined && foundErrorValue.count! > 1) {
				const updatedErrorValue = this.decreaseErrorItemCount(foundErrorValue);
				const sortedErrorEntries = this.filterErrorCodeItem(
					foundErrorValues,
					updatedErrorValue,
					foundErrorEntry,
					currentClientErrorEntries
				);

				// update error count label with amount of errors chosen
				const amountOfErrors = this.updateErrorCount(sortedErrorEntries);
				this.setState({
					clientErrorEntries: sortedErrorEntries,
					errorCount: amountOfErrors,
				});
				return;
			} else {
				const updatedErrorValues = foundErrorValues.filter(
					(err) => err.id !== foundErrorValue!.id
				);

				const updatedErrorEntry = {
					...foundErrorEntry,
					values: updatedErrorValues,
				};
				const indexOfClientErrorEntry = currentClientErrorEntries.findIndex(
					(err) => err.skillCategoryId === updatedErrorEntry.skillCategoryId
				);
				const updatedClientErrorEntries = currentClientErrorEntries;
				updatedClientErrorEntries[indexOfClientErrorEntry] = updatedErrorEntry;
				updatedClientErrorEntries.sort(
					(a, b) => a.skillCategoryId! - b.skillCategoryId!
				);

				// update error count label with amount of errors chosen
				const amountOfErrors = this.updateErrorCount(updatedClientErrorEntries);

				this.setState({
					clientErrorEntries: updatedClientErrorEntries,
					errorCount: amountOfErrors,
				});
				return;
			}
		}
	};

	private updateErrorCount = (errors: CoreErrorEntryDTO[]) => {
		let errorCount = 0;

		errors.forEach((error) => {
			error.values!.forEach((value) => {
				errorCount = errorCount + value.count!;
			});
		});

		return errorCount;
	};

	private backToPreviousPage = () => {
		const { datasetId } = this.props.match.params;
		const coreErrorEntry = {
			coreTestResultId: this.props.match.params.coreId,
			datasetId: datasetId,
			errorEntries: this.state.clientErrorEntries,
		} as UpsertClientSiteCoreErrorEntryRequest;

		CoreAPI.upsertClientSiteCoreErrorEntry(coreErrorEntry)
			.then(() => {
				this.setState({ isDirty: false });
			})
			.then(() => {
				this.props.history.push("skills-appraisal");
			})
			.catch(() => {
				toast.error("Something went wrong");
				this.setState({ completeClicked: false });
			});
	};

	private submitExcelerate = async () => {
		const { organizationId, datasetId, clientId, sessionId } =
			this.props.match.params;

		this.setState({ completeClicked: true });

		const coreErrorEntry = {
			coreTestResultId: this.props.match.params.coreId,
			datasetId: datasetId,
			errorEntries: this.state.clientErrorEntries,
		} as UpsertClientSiteCoreErrorEntryRequest;

		const url = `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/results`;

		try {
			const { scoringClient } = this.props.api;

			await CoreAPI.upsertClientSiteCoreErrorEntry(coreErrorEntry);
			await scoringClient.scoreAssessment(sessionId);
			this.setState({ isDirty: false });
			this.props.history.push(url);
			toast.success("Successfully completed On-Road");
		} catch {
			toast.error("Something went wrong");
			this.setState({ completeClicked: false });
		}
	};
}

export default CoreErrorEntry;
